import { toast } from '@oliasoft-open-source/react-ui-library';
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { apiCallBegan } from 'api/api';
import { ICostCodesItem, ICostCodesState } from 'src/common/interfaces/cost-codes';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import type { TAppDispatch, TGetState } from 'src/store/store-types';
import { checkForDuplicates } from './helpers/check-for-duplicates';

const COST_CODES_URL = '/api/cost-codes';

export const initialCostCodesItem: ICostCodesItem = {
  costCodesId: null,
  companyId: null,
  name: 'Name',
  code: 'Code',
  deleted: null,
  isCategory: false,
};

const initialState: ICostCodesState = {
  isAdding: false,
  isFetching: false,
  isUpdating: false,
  costCodesItems: [],
  error: null,
};

const costCodes = createSlice({
  name: 'costCodes',
  initialState,
  reducers: {
    getCostCodesRequested: (costCodes) => {
      costCodes.isFetching = true;
    },
    getCostCodesReceived: (costCodes, action: PayloadAction<{ costCodes: ICostCodesItem[] }>) => {
      costCodes.isAdding = false;
      costCodes.costCodesItems = action.payload.costCodes;
    },
    getCostCodesFailed: (costCodes) => {
      costCodes.isAdding = false;
    },
    addCostCodesItemRequested: (costCodes) => {
      costCodes.isAdding = true;
    },
    addCostCodesItemReceived: (costCodes, action: PayloadAction<{ costCodesItem: ICostCodesItem }>) => {
      costCodes.isAdding = false;
      costCodes.costCodesItems.push(action.payload.costCodesItem);
    },
    addCostCodesItemFailed: (costCodes) => {
      costCodes.isAdding = false;
    },
    costCodesCellValueUpdated: (
      costCodes,
      action: PayloadAction<{
        id: string;
        value: string;
        field: keyof ICostCodesItem;
      }>,
    ) => {
      const { id, value, field } = action.payload;
      const costCodesIndex = costCodes.costCodesItems.findIndex((item) => item.costCodesId === id);
      if (costCodesIndex !== -1) {
        costCodes.costCodesItems[costCodesIndex] = {
          ...costCodes.costCodesItems[costCodesIndex],
          [field]: value,
        };
      }
    },
    costCodesItemDeleted: (costCodes, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      costCodes.costCodesItems = costCodes.costCodesItems.filter((item) => item.costCodesId !== id);
    },
    updateCostCodesItemRequested: (costCodes) => {
      costCodes.isUpdating = true;
    },
    updateCostCodesItemReceived: (costCodes) => {
      costCodes.isUpdating = false;
      costCodes.error = null;
    },
    updateCostCodesItemFailed: (costCodes) => {
      costCodes.isUpdating = false;
    },
    updateCostCodesItemError: (costCodes, action) => {
      costCodes.error = action.payload;
    },
  },
});

export const {
  getCostCodesRequested,
  getCostCodesReceived,
  getCostCodesFailed,
  addCostCodesItemRequested,
  addCostCodesItemReceived,
  addCostCodesItemFailed,
  costCodesCellValueUpdated,
  costCodesItemDeleted,
  updateCostCodesItemRequested,
  updateCostCodesItemReceived,
  updateCostCodesItemFailed,
  updateCostCodesItemError,
} = costCodes.actions;

export default costCodes.reducer;

export const getCostCodesItems = () =>
  apiCallBegan({
    url: COST_CODES_URL,
    method: 'GET',
    onStart: getCostCodesRequested.type,
    onSuccess: getCostCodesReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.costCodes_failedToGetCostCodes) as string,
        },
      });
      return {
        type: getCostCodesFailed.type,
      };
    },
  });

export const addCostCodesItem = (data: ICostCodesItem) =>
  apiCallBegan({
    url: COST_CODES_URL,
    method: 'POST',
    data,
    onStart: addCostCodesItemRequested.type,
    onSuccess: addCostCodesItemReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.costCodes_failedToAddCostCodes) as string,
        },
      });
      return {
        type: addCostCodesItemFailed.type,
      };
    },
  });

export const deleteCostSetupItem = (id: string) =>
  apiCallBegan({
    url: `${COST_CODES_URL}/${id}`,
    method: 'DELETE',
    onSuccess: costCodesItemDeleted.type,
    onError: () =>
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.costCodes_failedToRemoveCostCodeItem) as string,
        },
      }),
  });

export const updateCostCodesItem =
  (id: string, data: ICostCodesItem) => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState();
    const items = state.entities.costCodes.costCodesItems.filter((item) => item.deleted === null);
    const hasDuplicate = checkForDuplicates(items, (item) => `${item.name}:${item.code}`);

    if (hasDuplicate.hasDuplicate) {
      return dispatch(updateCostCodesItemError(hasDuplicate.item));
    }

    dispatch(
      apiCallBegan({
        url: `${COST_CODES_URL}/${id}`,
        method: 'PUT',
        data,
        onStart: updateCostCodesItemRequested.type,
        onSuccess: updateCostCodesItemReceived.type,
        onError: () => {
          toast({
            message: {
              type: 'Error',
              content: i18n.t(translations.costCodes_failedToUpdateCostCodesItem) as string,
            },
          });
          return {
            type: updateCostCodesItemFailed.type,
          };
        },
      }),
    );
  };
